import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ArrowLeft, User, Mail, Phone, Shield, CreditCard, Calendar, DollarSign } from 'lucide-react';
import { latinAmericaCountries } from '../data/countries';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, license, logout } = useAuth();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    setLoading(true);
    try {
      // Si es seller, obtener datos de seller
      if (user.role === 'seller') {
        const response = await api.get('/api/sellers/me');
        setSeller(response.data);
      }

      // Obtener licencia si tiene
      if (user.license_id) {
        const licenseResponse = await api.get(`/api/licenses/${user.license_id}`);
        setLicense(licenseResponse.data);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPaymentReport = async (e) => {
    e.preventDefault();
    
    if (!paymentReceipt) {
      alert('Debes subir un comprobante');
      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Ingresa un monto válido');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('amount', paymentAmount);
      formData.append('notes', paymentNotes);
      formData.append('receipt', paymentReceipt);

      await api.post('/api/payment-reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('✅ Pago reportado exitosamente. Espera la verificación del administrador.');
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentReceipt(null);
      await loadUserData();
    } catch (error) {
      console.error('Error reporting payment:', error);
      alert('Error al reportar pago: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const getCountryName = (countryCode) => {
    const country = latinAmericaCountries.find(c => c.code === countryCode);
    return country ? `${country.flag} ${country.name}` : countryCode;
  };

  const getCountryDialCode = (countryCode) => {
    const country = latinAmericaCountries.find(c => c.code === countryCode);
    return country ? country.dialCode : '';
  };

  const getRoleName = (role) => {
    const roles = {
      'super_admin': 'Super Administrador',
      'admin': 'Administrador',
      'seller': 'Vendedor'
    };
    return roles[role] || role;
  };

  const getLicenseType = (type) => {
    const types = {
      'trial': 'Prueba',
      'paid': 'Pagada',
      'suspended': 'Suspendida'
    };
    return types[type] || type || 'N/A';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(user.role === 'seller' ? '/seller-sales' : '/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mi Perfil</h1>
              <p className="text-sm text-gray-600">Información de tu cuenta</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Información Personal */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-6 h-6 text-blue-600" />
              Información Personal
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Nombre Completo</p>
                <p className="font-semibold text-gray-900">{user.full_name}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Correo Electrónico</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {user.email}
                </p>
              </div>

              {user.phone && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Teléfono</p>
                  <p className="font-semibold text-gray-900 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {getCountryDialCode(user.country_code || 'MX')} {user.phone}
                  </p>
                </div>
              )}

              {user.country_code && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">País</p>
                  <p className="font-semibold text-gray-900">
                    {getCountryName(user.country_code)}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-600 mb-1">Rol</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-400" />
                  {getRoleName(user.role)}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Fecha de Registro</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {new Date(user.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Información de Licencia (Admin) */}
          {license && user.role === 'admin' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-green-600" />
                Información de Licencia
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Tipo de Licencia</p>
                  <p className={`font-semibold ${
                    license.type === 'trial' ? 'text-yellow-600' :
                    license.type === 'paid' ? 'text-green-600' :
                    'text-red-600'
                  }`}>
                    {getLicenseType(license?.type)?.toUpperCase() || 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Estado</p>
                  <p className={`font-semibold ${
                    license.is_active ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {license.is_active ? 'ACTIVA' : 'INACTIVA'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Routers Permitidos</p>
                  <p className="font-semibold text-gray-900">{license.max_routers}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Vendedores Permitidos</p>
                  <p className="font-semibold text-gray-900">{license.max_sellers}</p>
                </div>

                {license.activation_date && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Fecha de Activación</p>
                    <p className="text-sm text-gray-900">
                      {new Date(license.activation_date).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {license.expiry_date && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Fecha de Expiración</p>
                    <p className="text-sm text-gray-900">
                      {new Date(license.expiry_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Botón Reportar Pago Anticipado */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => navigate('/payment-summary')}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  <DollarSign className="w-5 h-5" />
                  Reportar Pago Anticipado
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Paga antes de tu fecha de vencimiento para evitar interrupciones
                </p>
              </div>
            </div>
          )}

          {/* Información de Vendedor */}
          {seller && user.role === 'seller' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-green-600" />
                Información de Vendedor
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Tipo de Vendedor</p>
                  <p className="font-semibold text-gray-900">
                    {seller.seller_type === 'acumulativo' ? 'Acumulativo' : 'Por Saldo'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Saldo Actual</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${seller.current_balance?.toFixed(2) || '0.00'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Comisión</p>
                  <p className="font-semibold text-gray-900">
                    {seller.commission_percentage}%
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Punto de Venta</p>
                  <p className="font-semibold text-gray-900">
                    {seller.pos_name || 'No asignado'}
                  </p>
                </div>
              </div>

              {seller.seller_type === 'por_saldo' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    <DollarSign className="w-5 h-5" />
                    Reportar Pago
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Botón Cerrar Sesión */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <button
              onClick={logout}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </main>

      {/* Modal de Reportar Pago */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold mb-6">💳 Reportar Pago</h3>
            
            <form onSubmit={handleSubmitPaymentReport} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto a Recargar *
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-3 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    required
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comprobante de Pago *
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  required
                  onChange={(e) => setPaymentReceipt(e.target.files[0])}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Formatos: JPG, PNG, PDF (máx 5MB)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notas (Opcional)
                </label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Transferencia BBVA, Folio: 123456"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Importante:</strong> Tu reporte será revisado por el administrador. El saldo se agregará una vez aprobado.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                    setPaymentNotes('');
                    setPaymentReceipt(null);
                  }}
                  disabled={processing}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50"
                >
                  {processing ? 'Enviando...' : 'Enviar Reporte'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
